import { AlertDialog, Box, Button, Flex, Image, Text, useDisclosure } from '@chakra-ui/react';
import Logo from 'assets/logowhite.png';
import CustomAlert from 'components/CustomAlert';
import OpenQuestion from 'components/Questions/OpenQuestion';
import ProgressCounter from 'components/Questions/ProgressCounter';
import QuestionComp from 'components/Questions/QuestionComp';
import useEvaluation from 'hooks/useEvaluation';
import { Fragment, useRef } from 'react';
import { FieldError, SubmitErrorHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import questions from './questions.json';

interface IParamsProps {
  id: string;
}

type Form = Record<number, string[][]>;

const getAnswers = (data: Form) => {
  const final: { question: string; response: string }[] = [];
  const pages = Object.values(data);

  pages.forEach((page, pageIndex) => {
    page.forEach((question, questionIndex) => {
      const questionNumber = `${questionIndex}`;
      if (typeof question === 'string') return final.push({ question: `${questionNumber}`, response: question });
      question &&
        question.forEach((answer, answerIndex) => {
          if (!answer) return;
          if (!answerIndex) {
            final.push({ question: `${questionNumber}`, response: answer });
          }
        });
    });
  });
  return final;
};

export default function QuestionPsychologicalSafety() {
  const { id } = useParams<IParamsProps>();
  const { handleSubmit, control, watch } = useForm<Form>({ shouldFocusError: true });
  const { registerResponses } = useEvaluation({ id });
  const { isOpen: isOpenStart, onClose: onCloseStart } = useDisclosure({
    defaultIsOpen: true,
  });
  const cancelRef = useRef(null);

  async function onSubmit(data: Form) {
    const answers = getAnswers(data);
    await registerResponses(answers);
  }

  const handleInvalid: SubmitErrorHandler<Form> = (evt) => {
    const pages = Object.values(evt) as unknown as FieldError[][];
    pages.forEach((page) => {
      if (!page) return;
      page.forEach((question: FieldError) => {
        if (!question) return;
        toast.error(question.message);
      });
    });
  };

  return (
    <Fragment>
      <AlertDialog isOpen={isOpenStart} leastDestructiveRef={cancelRef} onClose={onCloseStart} isCentered>
        <CustomAlert
          title="Leia atentamente as instruções antes de responder"
          onClose={() => {
            null;
          }}
          onNext={onCloseStart}
          hasCloseButton={false}
          nextText="Entendi"
        >
          <div>
            <ol style={{ paddingLeft: '1rem' }}>
              <li>Leia cada afirmação. </li>
              <li>Marque sua resposta de 1 (discordo totalmente) a 5 (concordo totalmente). </li>
              <li>Seja honesto. As respostas são confidenciais. </li>
            </ol>
            <br />
            {/* <b>Lembre-se: Não mude a ordem das forças. Use exatamente como estão no seu relatório.</b> */}
          </div>
        </CustomAlert>
      </AlertDialog>
      <Flex
        as="form"
        onSubmit={handleSubmit(onSubmit, handleInvalid)}
        w="100%"
        h="100vh"
        alignItems="center"
        flexDir="column"
        overflow="hidden"
      >
        <Flex overflowY="auto" overflowX="hidden" w="100%" flexDir="column" alignItems="center">
          <Box maxWidth={['100%', '100%', '62.5rem']}>
            <Flex justify="center" w={['100%']} bg="#f8bb03" flexDirection={['column']} padding="1.25rem">
              <Flex justify="space-around" align="center" w={['100%', '100%']} flexDirection={['column', 'row']}>
                <Image w={['11.25rem', '14.375rem']} h={['8.125rem', '9.375rem']} src={Logo} alt="Logo White" />
                <Text color="#FFF" maxW={'30rem'} textAlign="right" fontSize={['1.5rem', '2.3rem']}>
                  PERCEPÇÃO DE SEGURANÇA PSICOLÓGICA
                </Text>
              </Flex>
              <Box marginX="2.5rem" marginTop="1.25rem">
                <Text align="left" fontWeight="600">
                  Questionário com 7 afirmações, criado por Amy Edmondson (professora na Universidade de Harvard),
                  referência global em segurança psicológica. Você responderá de acordo com sua percepção sobre a
                  segurança psicológica na sua equipe.
                </Text>
              </Box>
            </Flex>
            <Flex margin={['0.625rem 2.5rem', '0.625rem 3.75rem']}>
              <Text color="red">*</Text>
              <Text>Obrigatória</Text>
            </Flex>
            <Box position="relative">
              {questions[0].questions.map(({ question }, index) => {
                return (
                  <QuestionComp
                    key={question}
                    question={{ text: question }}
                    index={index}
                    page={0}
                    control={control}
                    options={[
                      '1 - Discordo totalmente',
                      '2 - Discordo parcialmente',
                      '3 - Neutro',
                      '4 - Concordo parcialmente',
                      '5 - Concordo totalmente',
                    ]}
                  />
                );
              })}
              <OpenQuestion
                control={control}
                question={`Por favor, adicione quaisquer comentários que deseje compartilhar para detalhar ou
                            explicar suas respostas. Eles nos ajudarão a identificar maneiras de melhorar como
                            equipe.`}
                page={0}
                index={7}
                radio={false}
              />
            </Box>
          </Box>
        </Flex>
        <ProgressCounter
          maxWidth={['100%', '100%', '62.5rem']}
          width={'100%'}
          text="Respondidas"
          counter={watch()?.[0]?.reduce((acc, curr) => acc + (curr?.[0]?.length > 0 ? 1 : 0), 0) ?? 0}
          total={questions[0].questions.length + 1}
          bg="white"
        >
          <Button type="submit" w="10rem" bg="#f8bb03" color="#fff" fontWeight="500" fontSize="1.3rem">
            {0 < questions.length - 1 ? 'Avançar' : 'Enviar'}
          </Button>
        </ProgressCounter>
      </Flex>
    </Fragment>
  );
}
